import { EventClickArg, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateClickArg } from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { format, addDays } from "date-fns";
import React, { useState, useRef, useEffect } from "react";

import { useHandleCalendar } from "0.Mine/1.Hooks/business/CalendarEvent";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";

import { Vendor } from "./TaskModal";

interface MeetingScheduleModalProps {
  open: boolean;
  onClose: () => void;
  vendor: Vendor | null;
  leadName: string;
  onSchedule: (date: Date, title: string, duration: number) => Promise<void>;
}

const MeetingScheduleModal: React.FC<MeetingScheduleModalProps> = ({
  open,
  onClose,
  vendor,
  leadName,
  onSchedule,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const calendarRef = useRef<FullCalendar | null>(null);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [meetingTitle, setMeetingTitle] = useState(`Meeting${leadName ? ` con ${leadName}` : ""}`);
  const [meetingDuration, setMeetingDuration] = useState<number>(30);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meetingType, setMeetingType] = useState<string>("Meeting");

  const { session } = useBusinessContext();
  const { session: userSession } = useSession();

  const { getVendorCalendarEvents, sendEventToVendor } = useHandleCalendar();

  const [mockEvents, setMockEvents] = useState<EventInput[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (session && session.selectedBusiness && vendor) {
          console.log("Fetching events for vendor:", vendor.name, "ID:", vendor.id);
          const events = await getVendorCalendarEvents(session.selectedBusiness.id, vendor.id);
          console.log("API returned events:", events);

          if (events && Array.isArray(events)) {
            const newEvents: EventInput[] = [];
            events.forEach((event) => {
              // Log each event for debugging
              console.log("Processing event:", event.id, event.title);

              newEvents.push({
                id: event.id.toString(), // Ensure ID is a string
                title: event.title,
                start: new Date(event.start),
                end: new Date(event.end),
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                description: event.description,
                location: event.location,
              });
            });
            console.log("Transformed events for calendar:", newEvents);
            setMockEvents(newEvents);
          }
        }
      } catch (error) {
        console.error("Error fetching vendor calendar events:", error);
      }
    };

    fetchData();
  }, [session, vendor, session?.selectedBusiness?.id]); // Add vendor as dependency

  if (!session) {
    return <p>Loading...</p>;
  }

  const handleDateClick = (arg: DateClickArg) => {
    // Only allow selecting future dates
    const clickedDate = new Date(arg.date);
    const now = new Date();

    if (clickedDate < now) {
      return;
    }

    setSelectedDate(clickedDate);
    setSelectedSlot(format(clickedDate, "HH:mm"));
  };

  // Remove event_id and only use id
  const handleScheduleMeeting = async () => {
    if (!selectedDate || !selectedSlot) return;

    try {
      setIsSubmitting(true);

      // Parse the selected time
      const [hours, minutes] = selectedSlot.split(":").map(Number);
      const meetingDate = new Date(selectedDate);
      meetingDate.setHours(hours, minutes, 0, 0);

      const endDate = new Date(meetingDate.getTime() + meetingDuration * 60000);

      await onSchedule(meetingDate, meetingTitle, meetingDuration);

      // Create a unique ID
      const tempId = `temp-${Date.now()}`;

      // Create the new event without event_id
      const newEvent = {
        userId: userSession.user.id,
        id: tempId,
        title: meetingTitle,
        start: meetingDate,
        end: endDate,
        description: `Meeting scheduled with ${leadName}`,
        location: meetingType,
        remindOnWhatsapp: true,
        remindViaEmail: true,
        sharedWith: [],
        vendorId: vendor ? [vendor.id] : [],
      };

      // Update local calendar immediately
      setMockEvents((prev) => [
        ...prev,
        {
          id: tempId,
          title: newEvent.title,
          start: newEvent.start,
          end: newEvent.end,
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          description: newEvent.description,
          location: newEvent.location,
        },
      ]);

      // Send to API
      await sendEventToVendor(session.selectedBusiness.id, vendor.id, newEvent);

      onClose();
    } catch (error) {
      console.error("Failed to schedule meeting:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Available time slots for selection
  const timeSlots = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
  ];

  // Update the isTimeSlotAvailable function to check for past times on the current day
  const isTimeSlotAvailable = (timeSlot: string): boolean => {
    if (!selectedDate) return false;

    const [hours, minutes] = timeSlot.split(":").map(Number);
    const slotStartDate = new Date(selectedDate);
    slotStartDate.setHours(hours, minutes, 0, 0);

    // Check if the time slot is in the past
    const now = new Date();
    if (slotStartDate <= now) {
      return false; // Slot is in the past or current time
    }

    const slotEndDate = new Date(slotStartDate.getTime() + meetingDuration * 60000);

    // Check for conflicts with mock events
    for (const event of mockEvents) {
      const eventStart = new Date(event.start as string | Date);
      const eventEnd = new Date(event.end as string | Date);

      // Check if there's an overlap
      if (
        (slotStartDate >= eventStart && slotStartDate < eventEnd) ||
        (slotEndDate > eventStart && slotEndDate <= eventEnd) ||
        (slotStartDate <= eventStart && slotEndDate >= eventEnd)
      ) {
        return false;
      }
    }

    return true;
  };

  // Add the handler function for select events
  const handleDateSelect = (selectInfo: any) => {
    const startDate = new Date(selectInfo.start);
    const endDate = new Date(selectInfo.end);

    // Only allow selecting future dates/times
    const now = new Date();
    if (startDate <= now) {
      calendarRef.current?.getApi().unselect(); // Clear the selection
      return;
    }

    // Calculate exact duration in minutes
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    // Round to nearest 15 minutes (ceiling)
    const roundedDuration = Math.ceil(durationMinutes / 15) * 15;

    // Set values based on selection
    setSelectedDate(startDate);
    setSelectedSlot(format(startDate, "HH:mm"));
    setMeetingDuration(roundedDuration);
  };

  // Update the setSelectedSlot function to maintain the calendar selection
  const handleTimeSlotClick = (slot: string) => {
    // Parse the selected time
    const [hours, minutes] = slot.split(":").map(Number);
    const startDate = new Date(selectedDate);
    startDate.setHours(hours, minutes, 0, 0);

    // Ensure we're not selecting a past time
    const now = new Date();
    if (startDate <= now) {
      return; // Don't select past times
    }

    setSelectedSlot(slot);

    // Update the calendar's selected range if there's already a selected date
    if (selectedDate && calendarRef.current) {
      const calendar = calendarRef.current.getApi();

      // Calculate end date based on current meeting duration
      const endDate = new Date(startDate.getTime() + meetingDuration * 60000);

      // Clear any existing selection and select the new range
      calendar.unselect();
      calendar.select(startDate, endDate);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="meeting-schedule-modal">
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : "1200px",
          maxWidth: "95vh",
          maxHeight: "95vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.primary.main,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <EventIcon sx={{ mr: 1 }} />
            <Typography
              variant="h6"
              component="h2"
              sx={{ lineHeight: "31px", color: "white!important" }}
            >
              Pianifica Meeting {leadName ? `per ${leadName}` : ""}{" "}
              {vendor ? `assegnato a ${vendor.name}` : ""}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Calendar and Form Grid */}
        <Box
          sx={{
            p: 2,
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* Calendar section */}
          <Box
            sx={{
              flex: 1.8,
              height: isMobile ? "400px" : "600px",
              mr: isMobile ? 0 : 2,
              mb: isMobile ? 2 : 0,
            }}
            className="custom-calendar-meeting"
          >
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek",
              }}
              events={mockEvents}
              slotMinTime="08:00:00"
              slotMaxTime="18:00:00"
              allDaySlot={false}
              selectable={true} // This enables selection
              selectMirror={true}
              dayMaxEvents={false}
              weekends={false}
              dateClick={handleDateClick}
              // Add this handler for drag selection
              select={handleDateSelect}
              height="100%"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
                startTime: "09:00",
                endTime: "22:00",
              }}
              locale="it"
              buttonText={{
                today: "Oggi",
                month: "Mese",
                week: "Settimana",
              }}
            />
          </Box>

          {/* Form section */}
          <Box
            sx={{
              flex: 1,
              ml: isMobile ? 0 : 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1">Dettagli Meeting</Typography>

            <TextField
              fullWidth
              label="Titolo"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Tipologia</InputLabel>
              <Select
                value={meetingType}
                label="Tipologia"
                onChange={(e) => setMeetingType(e.target.value)}
              >
                <MenuItem value="In sede, dal cliente">In sede, dal cliente</MenuItem>
                <MenuItem value="In sede, dal commerciale">Nella mia attivita</MenuItem>
                <MenuItem value="Meeting">Videocall</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Durata</InputLabel>
              <Select
                value={meetingDuration}
                label="Durata"
                disabled // Make it read-only
              >
                <MenuItem value={meetingDuration}>
                  {meetingDuration === 60
                    ? "1 ora"
                    : meetingDuration === 90
                    ? "1 ora e 30 minuti"
                    : `${meetingDuration} minuti`}
                </MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom className="smallTitle-meeting">
                Data selezionata:
              </Typography>

              <Typography
                variant="body2"
                gutterBottom
                color="text.secondary"
                className="smallText-meeting"
              >
                {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Nessuna data selezionata"}
              </Typography>

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ mt: 2 }}
                className="smallTitle-meeting"
              >
                Orari disponibili:
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  maxHeight: "150px",
                  overflowY: "auto",
                  mt: 1,
                  background: "#f6f6fb",
                  padding: "10px 5px",
                  borderRadius: "8px",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                {selectedDate ? (
                  timeSlots.map((slot) => (
                    <Button
                      key={slot}
                      variant={selectedSlot === slot ? "contained" : "outlined"}
                      size="small"
                      disabled={!isTimeSlotAvailable(slot)}
                      onClick={() => handleTimeSlotClick(slot)}
                      sx={{
                        minHeight: "unset",
                        padding: "12px 30px",
                        height: "24px",
                        lineHeight: "14px",
                      }}
                    >
                      {slot}
                    </Button>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" className="smallText-meeting">
                    Seleziona una data per visualizzare gli orari disponibili
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: 1,
              }}
            >
              <Button onClick={onClose} color="inherit">
                Annulla
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedDate || !selectedSlot || isSubmitting}
                onClick={handleScheduleMeeting}
              >
                Conferma
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default MeetingScheduleModal;
